import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getServerURL }                                         from 'configureApi';
import apiConfig                                                from 'res-api-cli/build/config';
import reportError, { ErrorTags }                               from '../errorReporting';

const server = axios.create({ baseURL: getServerURL() })

export class NetworkError extends Error {
  constructor(message: string) {
    super(message)
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

export class CmsError extends NetworkError {
  code: number

  constructor(message: string, code: number) {
    super(message)
    this.code = code
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

/**
 * We allow auth to be handled by the current mechanism
 *
 * This appears to be packages/oso-spa/src/configureApp.js
 * using a global callback calling packages/oso-spa/src/lib/authData.js
 */
server.interceptors.request.use(
  (config) => {
    // @ts-expect-error apiConfig has not been set up well in old world code. We will re-write the auth config anyway
    config.headers.Authorization = apiConfig.headers.Authorization

    return config
  },
  error => Promise.reject(error)
)

export function registerAuthErrorHandler(onError: (error: AxiosError) => void) {
  server.interceptors.response.use(
    response => response,
    (error) => {
      if (error.response?.status === 401) {
        onError(error)
      }
      return Promise.reject(error)
    }
  )
}

const parseResponse = <R>(response: AxiosResponse<R>): R => {
  if (response.data) {
    return response.data
  }

  return response as unknown as R
}

const put = <IPayload, IResponse>(endpoint: string, payload: IPayload, config?: AxiosRequestConfig): Promise<IResponse> => server.put(endpoint, payload, config)
  .then((response: AxiosResponse<IResponse>) => parseResponse(response))
  .catch((error: AxiosError) => {
    throw handleException(error)
  })

const post = <IPayload, IResponse>(endpoint: string, payload: IPayload, config?: AxiosRequestConfig): Promise<IResponse> => server.post(endpoint, payload, config)
  .then((response: AxiosResponse<IResponse>) => parseResponse(response))
  .catch((error: AxiosError) => {
    throw handleException(error)
  })

const get = <IResponse>(endpoint: string, config?: AxiosRequestConfig): Promise<IResponse> => server.get(endpoint, config)
  .then((response: AxiosResponse<IResponse>) => parseResponse(response))
  .catch((error: AxiosError) => {
    throw handleException(error)
  })

const del = <IResponse>(endpoint: string, config?: AxiosRequestConfig): Promise<IResponse> => server.delete(endpoint, config)
  .then((response: AxiosResponse<IResponse>) => parseResponse(response))
  .catch((error: AxiosError) => {
    throw handleException(error)
  })

const handleException = (error: AxiosError) => {
  // Is network issue
  if (!error.response) {
    return new NetworkError(error.message || 'Network error')
  }

  if (error.response.status === 500) {
    reportError(
      error,
      'CMS API 500 Error',
      {
        context: {
          tags: `${ErrorTags.API_AXIOS}, ${error.response.status}, ${error?.config?.method ?? 'no-http-method'}`,
          url: error?.config?.url ?? 'no-url-set',
          status: error.response.status,
          data:   error.response.data
        }
      }
    );
    return new CmsError('There was an error', error.response.status)
  }

  // Standard cms error
  const message = (error.response as AxiosResponse)?.data?.message;
  const validationErrors = (error.response as AxiosResponse)?.data?.errors?.request;
  if (validationErrors) {
    return new CmsError(getValidationMessage(validationErrors), error.response.status)
  }
  if (message) {
    return new CmsError(message, error.response.status)
  }

  return new CmsError(error.message, error.response.status)
}

/**
 * Format validation errors into a string
 * @param validationErrors - The validation errors to format e.g. { field: 'error message' } or { field: ['error message 1', 'error message 2'] }
 * @returns A string with the validation errors formatted
 */
const getValidationMessage = (validationErrors: Record<string, string | string[]>) => {
  let validationMessage = ''
  for (const field in validationErrors) {
    const fieldMessage = validationErrors[field];
    if (Array.isArray(fieldMessage)) {
      validationMessage += `${field}: ${fieldMessage.join(', ')}\n`;
    } else {
      validationMessage += `${field}: ${fieldMessage}\n`;
    }
  }
  return validationMessage;
}

export default {
  get,
  put,
  post,
  delete: del
}
